/**
 * Coverage AutoFill (CAF-3) — `PROVIDER_REQUIREMENT_MAP`.
 *
 * Per-requirement registry of which upstream provider(s) can answer a
 * given Coverage Matrix requirement. Read by
 * `resolveRequirementCoverage` Tier 3 (provider-match) — the resolver
 * walks the entries for the requirement, filters by `geoScope` against
 * the site's country / state, asks each provider's `required(resp)`
 * predicate whether the cached response is non-empty, and returns the
 * first match.
 *
 * Risk-register R2 (DAM baselines from one region applied to a site in
 * a different region) is mitigated here for the provider tier: each
 * entry declares a `geoScope` and the resolver excludes entries whose
 * scope doesn't intersect the site. Mirrors the DCO Wave-B
 * `scoreGeoMatch` pattern (`src/services/dam/damToSlot.ts:152`).
 *
 * Hallucination guard (HARD RULE 8): every entry names a real provider
 * id from `PROVIDER_CATALOG` (src/services/dataProviders/index.ts) and
 * cites a public source URL the resolver stamps on the
 * `RequirementCoverage.sourceRef`. The resolver NEVER manufactures a
 * provider answer — `required(resp)` must return true before the entry
 * is selected.
 *
 * Out of scope for CAF-3:
 *   - The per-provider response-shape transforms (`toCoverage`) for the
 *     full Coverage Matrix surface — CAR-1..5 already ship those on
 *     `floodCoverageFill` / `utilityCoverageFill`; CAF-3 only declares
 *     the requirement→provider edges + selection metadata. CAF-5 wires
 *     the transforms into the resolver's tier-3 return shape.
 *
 * CAR-FOLLOWUP wave-18 (✅ SHIPPED):
 *   - CAR-FOLLOWUP-NREL — `solar-resource-summary` + `wind-resource-summary`
 *     edges wired to `nrel-solar` / `nrel-wind`. GeoScope `'global'`
 *     since NREL/NSRDB coverage spans most of the globe and the wind
 *     provider falls back to Open-Meteo.
 *   - Historical EIA averages supply context only, never tariff sufficiency.
 *     Former `retail-tariff-rate` edge removed; context is supplied by
 *     `eia-form-861`. The provider now issues a second EIA call
 *     against the v2 `retail-sales` endpoint (sector=IND, data=price)
 *     using state and sector; it does not identify a site-specific tariff.
 */

/** GeoScope grammar (mirrors `applyDamToSlot.scoreGeoMatch`). */
export type RequirementGeoScope =
  | 'global'
  | 'national-us'
  /** State-scoped: `'regional:<2-letter ISO country>-<2-letter state>'`. */
  | `regional:${string}`;

/** Site projection the geo-scope filter reads. */
export interface ProviderRequirementSite {
  /** ISO country code (uppercase). Defaults to `'US'` in the resolver
   *  when undefined (legacy sites carry no country). */
  countryCode?: string;
  /** Two-letter US state code (uppercase). */
  stateCode?: string;
}

/** Stable provider ids from `PROVIDER_CATALOG`. Re-declared as a string
 *  literal union here so a typo in the map is a compile error. */
export type RequirementProviderId =
  | 'fema-flood'
  | 'usfws-env'
  | 'usgs-seismic'
  | 'hifld-grid'
  | 'eia-electricity'
  | 'nrel-solar'
  | 'nrel-wind'
  | 'wri-aqueduct'
  | 'airnow-aqi'
  | 'nrc-reactors'
  | 'peeringdb-ix'
  | 'regrid-parcels'
  // CAR-1 — NRCS Web Soil Survey (SSURGO) drives the geotechnical
  // requirement edge. Keyless, US-only; provider source file:
  // src/services/dataProviders/nrcsSoilSurvey.ts.
  | 'nrcs-soil-survey'
  // CAR-3 — Title provider chain. Six providers under
  // `src/services/dataProviders/title/`. The chain composer picks
  // the strongest non-null payload via `mergeTitleData`.
  | 'regrid-title'
  | 'county-gis'
  | 'esri-living-atlas-parcels'
  | 'osm-landuse-title'
  | 'blm-sdms'
  | 'bia-tribal-lands'
  // CAR-4 — Zoning provider chain. Four providers under
  // `src/services/dataProviders/zoning/`. The chain composer picks
  // the strongest non-null payload via `mergeZoningData`. Municode
  // contributes overlay/special-use context (never a primary class).
  | 'county-gis-zoning'
  | 'esri-living-atlas-zoning'
  | 'municode-ordinance'
  | 'nlcd-landuse-zoning'
  // CAR-2 — Utility / interconnection provider chain. Five providers
  // under `src/services/dataProviders/utility/`. HIFLD is primary
  // (substation point geometry + transmission lines), EIA Form 861
  // secondary (utility-territory point-in-polygon + retail rates),
  // ISO/RTO queue tertiary (PJM/MISO/CAISO/SPP/NYISO/ISO-NE
  // dispatcher), FERC eLibrary supplementary (docket context), OASIS
  // real-time read-only ATC snapshots.
  | 'hifld-utility'
  | 'eia-form-861'
  | 'iso-queue'
  | 'ferc-elibrary'
  | 'oasis-real-time'
  // Gas-infra Tier 1 (2026-06) — EIA/HIFLD compressor-station /
  // interconnect / receipt-point proximity layers on the ORNL ArcGIS
  // host (the catalog entries in `src/config/arcgisLayerCatalog.ts`,
  // compiled into `gasInfraProximity` by
  // `compileOnSiteInfrastructure`). Context-only signal — NEVER
  // site-deliverable capacity (see the edge rationale).
  | 'hifld-gas-infra'
  // Gas-infra Tier 2 (2026-06) — PHMSA F7100.2-1 operator system stats
  // (bundled dataset `src/data/phmsaOperatorStats.json`, pure local join
  // in `src/services/dataProviders/phmsaOperatorStats.ts`; stamped onto
  // `fuelSupply.operatorSystemStats` by `buildFuelSupplyEvidence`).
  // Operator/state-level system context (diameter mix / %SMYS / age) —
  // NEVER site-deliverable capacity or pressure.
  | 'phmsa-operator-stats'
  // Gas-supply Tier 3a (2026-06) — EIA State-to-State pipeline capacity
  // workbook (bundled `src/data/eiaStateToStateCapacity.json`, pure local
  // lookup in `src/services/dataProviders/eiaPipelineCapacity.ts`; stamped
  // onto `fuelSupply.capacityContext` by `buildFuelSupplyEvidence`).
  // State-border interstate-corridor capacity CONTEXT — NEVER a
  // site-deliverable or firm capacity available to a new shipper.
  | 'eia-state-to-state-capacity'
  // Gas-supply Tier 3b (2026-06) — curated FERC-tariff gas-quality specs
  // (bundled `src/data/gasTariffQualitySpecs.json`, alias lookup in
  // `src/services/dataProviders/gasTariffQuality.ts`; stamped onto
  // `fuelSupply.qualitySpecs`). SCREENING-LEVEL HHV/CO2/inerts/H2S limits —
  // the operator's current effective tariff is authoritative.
  | 'gas-tariff-quality';

/**
 * One requirement → provider edge. Multiple edges may exist per
 * requirement (e.g. environmental survey can be answered by USFWS
 * habitat OR FEMA flood depending on the report scope); the resolver
 * tries them in declared order.
 */
export interface ProviderRequirementEdge {
  /** Provider id from `PROVIDER_CATALOG`. */
  provider: RequirementProviderId;
  /** Where on Earth this edge applies. The resolver filters out
   *  non-matching edges before evaluating `required`. */
  geoScope: RequirementGeoScope;
  /**
   * Predicate on the cached provider response — returns true when the
   * response is non-empty enough to answer the requirement. The
   * resolver short-circuits the rest of the edge list on the first
   * `required(resp) === true`.
   *
   * The argument is opaque to CAF-3 — the resolver supplies the typed
   * payload from `AggregatedSiteData[providerSlot]`. Each edge is
   * responsible for its own narrowing inside `required`.
   */
  required: (resp: unknown) => boolean;
  /**
   * Build the rationale + confidence floor the resolver stamps onto
   * the `RequirementCoverage`. Read at tier 3. Output mirrors the
   * shipped `mapFloodDataToCoverage` / `mapGridDataToCoverage`
   * narratives so the Coverage Matrix lineage tooltip reads
   * consistently across requirements.
   */
  toCoverage: (resp: unknown) => {
    rationale: string;
    /** 0..1 confidence floor. Test-pinned ≤ 0.85 (provider data is
     *  always weaker than an uploaded doc — pack §CAF-2 invariant). */
    confidence: number;
  };
  /**
   * Optional public source URL. Stamped on the `RequirementCoverage`
   * lineage tooltip as the canonical citation. When omitted the
   * resolver falls back to the provider's catalog URL.
   */
  sourceUrl?: string;
  /**
   * Note for the auditor explaining when this edge is preferred over
   * its siblings. Surfaced in the lineage inspector's "tried-and-
   * rejected" badge for fall-through tiers.
   */
  rationale?: string;
}

// ── helpers ───────────────────────────────────────────────────────────

/**
 * Test whether `siteScope` (site's country / state) satisfies `edge`.
 *
 * Rules:
 *   - `geoScope: 'global'`               → always matches.
 *   - `geoScope: 'national-us'`          → matches when site.countryCode === 'US' (defaults to US).
 *   - `geoScope: 'regional:US-LA'`       → matches when site is US + state matches.
 *   - `geoScope: 'regional:XX-YY'` for non-US country → matches when both halves agree.
 *
 * Returns false for any other shape. Exported for the resolver +
 * tests; the resolver applies it via `Array.filter` before evaluating
 * `required`.
 */
export function matchesGeoScope(
  geoScope: RequirementGeoScope,
  site: ProviderRequirementSite,
): boolean {
  const country = (site.countryCode ?? 'US').toUpperCase();
  if (geoScope === 'global') return true;
  if (geoScope === 'national-us') return country === 'US';
  // regional:<COUNTRY>-<STATE>
  const m = geoScope.match(/^regional:([A-Za-z]{2})-([A-Za-z]{2})$/);
  if (m) {
    const [, scopeCountry, scopeState] = m;
    if (scopeCountry!.toUpperCase() !== country) return false;
    if (!site.stateCode) return false;
    return scopeState!.toUpperCase() === site.stateCode.toUpperCase();
  }
  return false;
}

// ── the map ───────────────────────────────────────────────────────────

/**
 * Stable id of every checklist requirement that has at least one
 * provider edge. The keys mirror `ChecklistItem.id` in
 * `src/config/documentChecklists.ts`.
 *
 * Requirements WITHOUT a provider edge (e.g. `traffic-impact-study`,
 * `phase1-esa` — these need a document) are absent. The
 * resolver short-circuits tier 3 for missing keys.
 *
 * CAR-1 (2026-05-19) added the `'geotech'` edge wired to
 * `'nrcs-soil-survey'`; coverage is national-us because SSURGO is
 * CONUS + AK + HI only.
 *
 * Pack §CAF-3 calls out the following entries; we include all of them
 * that map to a shipped provider with a meaningful predicate, and
 * leave the rest as `CAF-FOLLOWUP-*`.
 */
export const PROVIDER_REQUIREMENT_MAP: ReadonlyMap<
  string,
  ReadonlyArray<ProviderRequirementEdge>
> = new Map<string, ReadonlyArray<ProviderRequirementEdge>>([
  // ── flood evidence ────────────────────────────────────────────────
  // Two checklist id variants exist in DOC_CHECKLISTS: 'flood-cert' is
  // the feasibility id, 'flood-elevation-certificate' is the pack-
  // brief id. Both map to the same FEMA NFHL edge; the resolver
  // selects on string match.
  ['flood-cert', [
    {
      provider: 'fema-flood',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const z = (resp as { femaZone?: unknown }).femaZone;
        return typeof z === 'string' && z !== '' && z !== 'UNMAPPED';
      },
      toCoverage: (resp) => {
        const zone = (resp as { femaZone?: string }).femaZone ?? 'unknown';
        const sfha = (resp as { isSpecialFloodHazard?: boolean }).isSpecialFloodHazard;
        return {
          rationale: `FEMA NFHL zone ${zone}${sfha ? ' (Special Flood Hazard Area)' : ''}`,
          // X-zone = "no flood" is high-confidence; SFHA zones are
          // lower-confidence because a real Flood Elevation
          // Certificate is still required.
          confidence: zone === 'X' ? 0.85 : 0.7,
        };
      },
      sourceUrl: 'https://msc.fema.gov/portal/home',
      rationale:
        'FEMA National Flood Hazard Layer — authoritative federal flood-zone designation for the parcel.',
    },
  ]],
  ['flood-elevation-certificate', [
    // W22-8 — CAR-FLOOD-WIRING. Stronger edge declared FIRST so the
    // resolver short-circuits on the BFE + zone combination when the
    // FEMA NFHL panel scrape returned both. The Flood Elevation
    // Certificate requirement specifically needs BFE + datum + panel
    // + effective-date; `bfeFt` is the load-bearing signal that the
    // upstream NFHL panel actually produced a number, not just a
    // zone classification. The downstream `floodCoverageFill` writes
    // the full `bfeFt/datum/panelId/effectiveDate` field-bag — see
    // `src/services/dataProviders/flood/coverage.ts` mapFloodDataToCoverage.
    //
    // Both `floodZone` (the adapter-coerced key the Coverage Matrix
    // uses) and `femaZone` (legacy resolver fixture key) are accepted,
    // with `Number.isFinite()` guarding the BFE value per HARD RULE
    // — NFHL panel scrapes can occasionally surface NaN when a FIRM
    // tile is corrupt and we never want a non-finite BFE to drive a
    // fill.
    {
      provider: 'fema-flood',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { bfeFt?: unknown; floodZone?: unknown; femaZone?: unknown };
        if (!Number.isFinite(r.bfeFt)) return false;
        const zone = typeof r.floodZone === 'string' ? r.floodZone : r.femaZone;
        return typeof zone === 'string' && zone !== '' && zone !== 'UNMAPPED';
      },
      toCoverage: (resp) => {
        const r = resp as {
          bfeFt?: number;
          floodZone?: string;
          femaZone?: string;
          datum?: string;
          panelId?: string;
        };
        const zone = r.floodZone ?? r.femaZone ?? 'unknown';
        const bfe = typeof r.bfeFt === 'number' ? r.bfeFt.toFixed(1) : '?';
        const datum = r.datum ?? 'NAVD88';
        const panel = r.panelId ? `, panel ${r.panelId}` : '';
        return {
          rationale: `FEMA NFHL panel + BFE resolves the certificate fields — zone ${zone}, BFE ${bfe} ft ${datum}${panel}.`,
          confidence: 0.75,
        };
      },
      sourceUrl: 'https://msc.fema.gov/portal/home',
      rationale:
        'FEMA NFHL panel + BFE — fills the Flood Elevation Certificate fields (BFE, datum, panel, effective date).',
    },
    // Fallback edge — zone-only (no BFE). Lower-confidence row that
    // still earns a chip so the user sees "we know the zone but the
    // cert needs a real elevation". Pre-W22-8 behaviour, kept so sites
    // without a published BFE on the panel still surface an auto-fill
    // option.
    {
      provider: 'fema-flood',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const z = (resp as { femaZone?: unknown }).femaZone;
        return typeof z === 'string' && z !== '' && z !== 'UNMAPPED';
      },
      toCoverage: (resp) => {
        const zone = (resp as { femaZone?: string }).femaZone ?? 'unknown';
        const sfha = (resp as { isSpecialFloodHazard?: boolean }).isSpecialFloodHazard;
        return {
          rationale: `FEMA NFHL zone ${zone}${sfha ? ' (Special Flood Hazard Area)' : ''}`,
          confidence: zone === 'X' ? 0.85 : 0.7,
        };
      },
      sourceUrl: 'https://msc.fema.gov/portal/home',
      rationale:
        'FEMA National Flood Hazard Layer — authoritative federal flood-zone designation for the parcel.',
    },
  ]],

  // ── wetlands survey ───────────────────────────────────────────────
  ['wetland', [
    {
      provider: 'usfws-env',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { wetlands?: unknown; hasWetlands?: unknown; wetlandMappingCoverage?: { status?: unknown } };
        return (Array.isArray(r.wetlands) && r.wetlands.length > 0)
          || r.hasWetlands === true
          || (r.hasWetlands === false && r.wetlandMappingCoverage?.status === 'complete');
      },
      toCoverage: () => ({
        rationale:
          'USFWS National Wetlands Inventory screening for the query footprint; mapped presence or covered absence is not a jurisdictional delineation.',
        confidence: 0.7,
      }),
      sourceUrl: 'https://www.fws.gov/program/national-wetlands-inventory',
      rationale:
        'USFWS NWI supplies mapped wetland screening; source mapping coverage and a site-specific delineation remain separate requirements.',
    },
  ]],

  // ── species survey ────────────────────────────────────────────────
  ['species-survey', [
    {
      provider: 'usfws-env',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const s = (resp as { species?: unknown }).species;
        return Array.isArray(s) && s.length > 0;
      },
      toCoverage: (resp) => {
        const s = (resp as { species?: unknown[] }).species;
        const n = Array.isArray(s) ? s.length : 0;
        return {
          rationale: `USFWS IPaC: ${n} ESA-listed species potentially present within parcel envelope.`,
          confidence: 0.7,
        };
      },
      sourceUrl: 'https://ipac.ecosphere.fws.gov/',
      rationale:
        'USFWS IPaC is the federal trip-wire for ESA §7 consultation — site survey remains required.',
    },
  ]],

  // ── seismic design basis ──────────────────────────────────────────
  ['seismic-design-basis', [
    {
      provider: 'usgs-seismic',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { sds?: unknown; pga?: unknown };
        return typeof r.sds === 'number' || typeof r.pga === 'number';
      },
      toCoverage: (resp) => {
        const r = resp as { sds?: number; sd1?: number; pga?: number; seismicDesignCategory?: string };
        const parts: string[] = [];
        if (typeof r.sds === 'number') parts.push(`SDS ${r.sds.toFixed(2)}g`);
        if (typeof r.sd1 === 'number') parts.push(`SD1 ${r.sd1.toFixed(2)}g`);
        if (r.seismicDesignCategory) parts.push(`SDC ${r.seismicDesignCategory}`);
        return {
          rationale: `USGS ASCE 7-22 design parameters: ${parts.join(' · ') || 'available'}.`,
          confidence: 0.8,
        };
      },
      sourceUrl: 'https://earthquake.usgs.gov/ws/designmaps/',
      rationale:
        'USGS publishes the ASCE 7-22 design-basis parameters used by every US jurisdiction; geotechnical investigation refines but does not replace.',
    },
  ]],

  // ── water rights / aquifer signal ─────────────────────────────────
  ['water-rights', [
    {
      provider: 'wri-aqueduct',
      geoScope: 'global',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { waterStressScore?: unknown; waterStressCategory?: unknown };
        return typeof r.waterStressScore === 'number' || typeof r.waterStressCategory === 'string';
      },
      toCoverage: (resp) => {
        const r = resp as { waterStressScore?: number; waterStressCategory?: string };
        const cat = r.waterStressCategory ?? 'unknown';
        const score =
          typeof r.waterStressScore === 'number' ? r.waterStressScore.toFixed(2) : '?';
        return {
          rationale: `WRI Aqueduct regional modeled water stress: ${cat} (${score}). Consult the source edition and coverage in the SED. Site-specific water rights filing still required.`,
          confidence: 0.6,
        };
      },
      sourceUrl: 'https://www.wri.org/aqueduct/data',
      rationale:
        'Aqueduct supplies edition-qualified historical basin screening context; legal water-rights provenance still requires state filings (CAF-FOLLOWUP-WATER-RIGHTS-REGIONAL).',
    },
  ]],

  // Utility letters require site-specific documents/authored evidence. HIFLD
  // location/voltage observations remain grid context, not offered service.

  // ── air-quality permitting signal ─────────────────────────────────
  ['airnow', [
    {
      provider: 'airnow-aqi',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { aqi?: unknown };
        return typeof r.aqi === 'number';
      },
      toCoverage: (resp) => {
        const r = resp as { aqi?: number; pollutant?: string };
        return {
          rationale: `AirNow AQI ${r.aqi ?? '?'}${r.pollutant ? ' (' + r.pollutant + ')' : ''} — EPA permitting baseline.`,
          confidence: 0.7,
        };
      },
      sourceUrl: 'https://docs.airnowapi.org/',
      rationale: 'EPA AirNow is the federal-grade AQI for Title V permitting.',
    },
  ]],

  // EIA state averages are context, not an applicable retail tariff or PPA.
  // Neither legacy `tariff` nor `retail-tariff-rate` has an EIA sufficiency edge.

  // ── geotechnical evidence (CAR-1) ─────────────────────────────────
  // NRCS Web Soil Survey (SSURGO) is the federal authoritative soil
  // map for the US. Resolver tier-3 reads
  // `args.providerResponses['geotechnical']` against this edge.
  //
  // The CAR-1 transform deliberately fills 3 of 4 authoredSchema fields
  // (`soilClassification`, `bearingCapacity_kPa`, `groundwaterDepth_m`)
  // and leaves `boreholeDepth_m` blank — SSURGO has no boring-log data,
  // so claiming a borehole depth would mislead the report. The notes
  // string flags that gap, and a real Phase II geotechnical
  // investigation remains the COL-grade requirement.
  ['geotech', [
    {
      provider: 'nrcs-soil-survey',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        // We require a mukey + at least one substantive field. The
        // mukey alone proves coverage; the substantive field is what
        // gives the report a non-empty narrative.
        const r = resp as {
          mukey?: unknown;
          uscsClass?: unknown;
          depthToWaterTableCm?: unknown;
        };
        if (typeof r.mukey !== 'string' || r.mukey === '') return false;
        return typeof r.uscsClass === 'string' || typeof r.depthToWaterTableCm === 'number';
      },
      toCoverage: (resp) => {
        const r = resp as {
          mukey?: string;
          mapUnitName?: string | null;
          uscsClass?: string | null;
          depthToWaterTableCm?: number | null;
        };
        const parts: string[] = [];
        parts.push(`NRCS SSURGO map unit ${r.mukey ?? '?'}`);
        if (r.mapUnitName) parts.push(`"${r.mapUnitName}"`);
        if (r.uscsClass) parts.push(`USCS ${r.uscsClass}`);
        if (typeof r.depthToWaterTableCm === 'number') {
          parts.push(`GW ${(r.depthToWaterTableCm / 100).toFixed(1)}m`);
        }
        return {
          // SSURGO is authoritative for soil texture / drainage / water-
          // table but NOT a substitute for a boring log. Cap at 0.6
          // (medium) per pack §CAR-8: provider-direct fills only earn
          // 'high' (0.75) when EVERY structured field is populated. The
          // borehole-depth gap means we max out at medium.
          rationale: `${parts.join(' · ')}. Real geotechnical investigation still required.`,
          confidence: 0.6,
        };
      },
      sourceUrl: 'https://websoilsurvey.sc.egov.usda.gov/App/HomePage.htm',
      rationale:
        'NRCS Web Soil Survey (SSURGO) — federal authoritative soil map; supplies USCS class + groundwater depth pre-borehole.',
    },
  ]],

  // ── parcel evidence (CAR-3 title chain) ───────────────────────────
  // CAR-3 wired six providers + an inferred-easement / OCR'd-deed
  // step. The resolver tier-3 walks edges in declared order, so we
  // put the highest-fidelity sources first (Regrid + County GIS),
  // then Esri Living Atlas, then OSM as international fallback.
  // BLM + Tribal don't FILL the parcel-survey row — they STAMP
  // banners (federal-land / Section 106) onto the notes via
  // `mapTitleDataToCoverage`. They're still listed here so the
  // resolver fires their `required` predicate for telemetry; their
  // `toCoverage` returns a low-confidence "banner only" rationale.
  ['parcel-survey', [
    {
      provider: 'regrid-title',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { parcelId?: unknown; recordedAcreage?: unknown };
        return typeof r.parcelId === 'string' || typeof r.recordedAcreage === 'number';
      },
      toCoverage: (resp) => {
        const r = resp as { parcelId?: string | null; recordedAcreage?: number; zoning?: string | null };
        return {
          rationale: `Regrid title — ${r.parcelId ?? '(no APN)'}: ${r.recordedAcreage ?? '?'} acres${r.zoning ? ' · ' + r.zoning : ''}. Legal ALTA survey still required.`,
          confidence: 0.65,
        };
      },
      sourceUrl: 'https://regrid.com/api',
      rationale: 'Regrid Parcels API — paid, US-national authoritative parcel attribute source.',
    },
    {
      provider: 'county-gis',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { parcelId?: unknown };
        return typeof r.parcelId === 'string' && r.parcelId.length > 0;
      },
      toCoverage: (resp) => {
        const r = resp as { parcelId?: string; recordedAcreage?: number };
        return {
          rationale: `County Assessor GIS — parcel ${r.parcelId ?? '?'} (${r.recordedAcreage ?? '?'} acres). Per-county shape; legal ALTA survey still required.`,
          confidence: 0.6,
        };
      },
      rationale: 'County Assessor GIS — free per-county source; lower fidelity than Regrid but covers counties Regrid lacks.',
    },
    {
      provider: 'esri-living-atlas-parcels',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { parcelId?: unknown; recordedAcreage?: unknown };
        return typeof r.parcelId === 'string' || typeof r.recordedAcreage === 'number';
      },
      toCoverage: (resp) => {
        const r = resp as { parcelId?: string | null; recordedAcreage?: number };
        return {
          rationale: `Esri Living Atlas parcels (Latest) — ${r.parcelId ?? '?'}, ${r.recordedAcreage ?? '?'} acres. Quarterly-refreshed; legal ALTA survey still required.`,
          confidence: 0.55,
        };
      },
      sourceUrl: 'https://livingatlas.arcgis.com/',
      rationale: 'Esri Living Atlas — free-with-AGO US-national fallback.',
    },
    {
      provider: 'osm-landuse-title',
      geoScope: 'global',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { landUse?: unknown; boundary?: unknown };
        return typeof r.landUse === 'string' || Array.isArray(r.boundary);
      },
      toCoverage: (resp) => {
        const r = resp as { landUse?: string | null };
        return {
          rationale: `OpenStreetMap landuse=${r.landUse ?? '?'} — boundary inferred, NOT legal. ALTA survey required.`,
          // OSM is the lowest-fidelity source; clamp to 0.5 → tier
          // 'low' in the confidence helper. The resolver still
          // hard-clamps to 0.85 in safeToCoverage.
          confidence: 0.5,
        };
      },
      sourceUrl: 'https://www.openstreetmap.org/copyright',
      rationale: 'OpenStreetMap landuse — international fallback only; never a substitute for a real parcel record.',
    },
    {
      provider: 'blm-sdms',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { blmFederalLand?: unknown };
        // Fire only when BLM said TRUE (banner trigger). False ≠
        // signal worth surfacing on its own.
        return r.blmFederalLand === true;
      },
      toCoverage: () => ({
        rationale: 'BLM SDMS — FEDERAL LAND. FLPMA Title V ROW grant + NEPA EA/EIS required.',
        confidence: 0.7,
      }),
      sourceUrl: 'https://gis.blm.gov/',
      rationale: 'BLM Surface Management Agency overlay — flags federal-land development gates.',
    },
    {
      provider: 'bia-tribal-lands',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { tribalOverlap?: unknown };
        return r.tribalOverlap === true;
      },
      toCoverage: () => ({
        rationale: 'BIA Tribal Lands — TRIBAL OVERLAP DETECTED. NHPA Section 106 consultation required (36 CFR 800). Defer to NRC ADAMS for any prior determination.',
        confidence: 0.75,
      }),
      sourceUrl: 'https://biamaps.geoplatform.gov/',
      rationale: 'BIA Tribal Lands — pack risk register R10 trigger; never silently auto-fill over tribal land.',
    },
  ]],

  // ── zoning evidence (CAR-4 zoning chain) ──────────────────────────
  // CAR-4 wired four providers + a chain composer / coverage transform.
  // Resolver tier-3 walks edges in declared order: county-GIS → Esri →
  // NLCD (raster fallback) for the primary class; municode contributes
  // OVERLAY context (special-use + conservation), never a primary class.
  //
  // Three requirements share the chain:
  //   - `zoning-confirmation` — primaryClass + parcelDistrict
  //   - `special-use-permit`  — overlays.special-use + municodeRef
  //   - `conservation-overlay`— overlays.conservation + isProtected
  //
  // We also alias the existing `'zoning'` checklist id (from
  // `documentChecklists.ts`) onto the same edges so the resolver covers
  // both the pack-level naming (kebab-tier) and the legacy id without
  // a checklist migration.
  //
  // NLCD edge ALWAYS returns a low confidence — the `mapZoningDataToCoverage`
  // transform additionally clamps NLCD-only contributions to ≤ 0.55, but
  // for the per-edge `toCoverage` rationale we also emit 0.5 (low) so the
  // resolver renders a consistent tier without depending on the chain
  // composer.
  ['zoning-confirmation', [
    {
      provider: 'regrid-parcels',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { zoning?: unknown };
        if (typeof r.zoning !== 'string' || r.zoning.trim().length === 0) return false;
        const z = r.zoning.trim().toLowerCase();
        return z !== 'unknown' && z !== 'n/a' && z !== 'na';
      },
      toCoverage: (resp) => {
        const r = resp as { zoning?: string; landUse?: string };
        return {
          rationale:
            `Regrid parcel metadata — zoning ${r.zoning ?? '?'}`
            + (r.landUse ? `, land use ${r.landUse}` : '')
            + '. May be stale; municipal verification required.',
          confidence: 0.5,
        };
      },
      sourceUrl: 'https://regrid.com/api',
      rationale: 'Regrid Parcels API — parcel-attached zoning when county GIS is unavailable.',
    },
    {
      provider: 'county-gis-zoning',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { primaryClass?: unknown };
        return typeof r.primaryClass === 'string' && r.primaryClass.length > 0;
      },
      toCoverage: (resp) => {
        const r = resp as { primaryClass?: string; parcelDistrict?: string | null };
        return {
          rationale: `County GIS Zoning — ${r.primaryClass ?? '?'}${r.parcelDistrict ? ' (' + r.parcelDistrict + ')' : ''}. Verify with municipal code BEFORE relying on the slot.`,
          confidence: 0.65,
        };
      },
      sourceUrl: 'https://library.municode.com/',
      rationale: 'County GIS Zoning Layer — free per-county source; highest-fidelity public layer when the county has a published GIS.',
    },
    {
      provider: 'esri-living-atlas-zoning',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { primaryClass?: unknown };
        return typeof r.primaryClass === 'string' && r.primaryClass.length > 0;
      },
      toCoverage: (resp) => {
        const r = resp as { primaryClass?: string; parcelDistrict?: string | null };
        return {
          rationale: `Esri Living Atlas Zoning (Beta) — ${r.primaryClass ?? '?'}${r.parcelDistrict ? ' (' + r.parcelDistrict + ')' : ''}. BETA layer; verify against county GIS.`,
          confidence: 0.55,
        };
      },
      sourceUrl: 'https://livingatlas.arcgis.com/',
      rationale: 'Esri Living Atlas Zoning (Beta) — US-national aggregator; lower-fidelity than per-county GIS.',
    },
    {
      provider: 'nlcd-landuse-zoning',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { landUseClass?: unknown };
        return typeof r.landUseClass === 'string' && r.landUseClass.length > 0;
      },
      toCoverage: (resp) => {
        const r = resp as { landUseClass?: string };
        return {
          rationale: `NLCD raster fallback — ${r.landUseClass ?? '?'} (30 m classification, NOT parcel-level zoning). Cap ≤ 0.55 per CAR-4 hard rule.`,
          confidence: 0.45,
        };
      },
      sourceUrl: 'https://www.mrlc.gov/data/nlcd-2021-land-cover-conus',
      rationale: 'NLCD / MRLC 30 m raster classification — fallback only when no parcel-level zoning provider returns data.',
    },
  ]],
  // Alias: existing `'zoning'` checklist id (from `documentChecklists.ts`)
  // shares the same edge list. Re-declared rather than referenced so the
  // `ReadonlyMap` literal type checks; the inner arrays are not mutated
  // anywhere downstream.
  ['zoning', [
    {
      provider: 'regrid-parcels',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { zoning?: unknown };
        if (typeof r.zoning !== 'string' || r.zoning.trim().length === 0) return false;
        const z = r.zoning.trim().toLowerCase();
        return z !== 'unknown' && z !== 'n/a' && z !== 'na';
      },
      toCoverage: (resp) => {
        const r = resp as { zoning?: string; landUse?: string };
        return {
          rationale:
            `Regrid parcel metadata — zoning ${r.zoning ?? '?'}`
            + (r.landUse ? `, land use ${r.landUse}` : '')
            + '. May be stale; municipal verification required.',
          confidence: 0.5,
        };
      },
      sourceUrl: 'https://regrid.com/api',
      rationale: 'CAR-4 alias — Regrid parcel zoning for the `zoning` checklist id.',
    },
    {
      provider: 'county-gis-zoning',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { primaryClass?: unknown };
        return typeof r.primaryClass === 'string' && r.primaryClass.length > 0;
      },
      toCoverage: (resp) => {
        const r = resp as { primaryClass?: string; parcelDistrict?: string | null };
        return {
          rationale: `County GIS Zoning — ${r.primaryClass ?? '?'}${r.parcelDistrict ? ' (' + r.parcelDistrict + ')' : ''}.`,
          confidence: 0.65,
        };
      },
      sourceUrl: 'https://library.municode.com/',
      rationale: 'CAR-4 alias for the existing `zoning` checklist id.',
    },
  ]],

  ['special-use-permit', [
    {
      provider: 'municode-ordinance',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { overlays?: unknown; municodeRef?: unknown };
        // Municode contributes when EITHER an overlay was detected OR
        // a municodeRef anchor section was extracted. The fill
        // transform downstream further filters to special-use overlays.
        const overlays = Array.isArray(r.overlays) ? r.overlays : [];
        const hasSpecialUse = overlays.some(
          (o) => o && typeof o === 'object' && (o as { kind?: unknown }).kind === 'special-use',
        );
        return hasSpecialUse || (r.municodeRef !== null && typeof r.municodeRef === 'object');
      },
      toCoverage: (resp) => {
        const r = resp as {
          overlays?: Array<{ kind?: string; name?: string }>;
          municodeRef?: { library?: string; section?: string } | null;
        };
        const su = (r.overlays ?? []).filter((o) => o.kind === 'special-use');
        const muni = r.municodeRef;
        const parts: string[] = [];
        if (su.length > 0) parts.push(`${su.length} special-use reference(s) detected`);
        if (muni) parts.push(`${muni.library ?? ''} ${muni.section ?? ''}`.trim());
        return {
          rationale: `Municode — ${parts.join(' · ')}. Confirm timeline + cost with jurisdiction planning office (DAM zone-002: 90-180 days typical).`,
          confidence: 0.55,
        };
      },
      sourceUrl: 'https://library.municode.com/',
      rationale: 'Municode ordinance scrape — overlay + special-use context for the special-use-permit row.',
    },
    {
      provider: 'county-gis-zoning',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { overlays?: unknown };
        const overlays = Array.isArray(r.overlays) ? r.overlays : [];
        // The county may flag overlay names on the primary zoning feature
        // (when the layer publishes an overlay field). We fire on the
        // catch-all 'other' kind too because a flat overlay attribute
        // does not carry a kind discriminator.
        return overlays.length > 0;
      },
      toCoverage: (resp) => {
        const r = resp as { overlays?: Array<{ name?: string }> };
        const count = (r.overlays ?? []).length;
        return {
          rationale: `County GIS Zoning — ${count} overlay reference(s) on the parcel zoning feature.`,
          confidence: 0.6,
        };
      },
      rationale: 'County GIS Zoning fallback when Municode has no jurisdiction slug registered.',
    },
  ]],

  ['conservation-overlay', [
    {
      provider: 'municode-ordinance',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { overlays?: unknown; isProtected?: unknown };
        const overlays = Array.isArray(r.overlays) ? r.overlays : [];
        const hasConservation = overlays.some(
          (o) => o && typeof o === 'object' && (o as { kind?: unknown }).kind === 'conservation',
        );
        return hasConservation || r.isProtected === true;
      },
      toCoverage: (resp) => {
        const r = resp as {
          overlays?: Array<{ kind?: string; name?: string }>;
          isProtected?: boolean | null;
        };
        const co = (r.overlays ?? []).filter((o) => o.kind === 'conservation');
        return {
          rationale:
            (r.isProtected === true ? 'CONSERVATION OVERLAY DETECTED. ' : '') +
            `${co.length} conservation overlay reference(s) via Municode. ` +
            `Cross-check FEMA NFHL + USFWS IPaC before commitment (DAM zone-003).`,
          confidence: 0.6,
        };
      },
      sourceUrl: 'https://library.municode.com/',
      rationale: 'Municode ordinance scrape — conservation overlay context for the conservation-overlay row.',
    },
    {
      provider: 'county-gis-zoning',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { isProtected?: unknown };
        return r.isProtected === true;
      },
      toCoverage: () => ({
        rationale:
          'County GIS Zoning — CONSERVATION FLAG on the parcel zoning feature. Confirm with holding entity (USFWS / state DEP / land trust).',
        confidence: 0.65,
      }),
      rationale: 'County GIS Zoning conservation-flag field — additional signal when published by the county.',
    },
  ]],

  // ── utility / interconnection chain (CAR-2) ──────────────────────
  // CAR-2 wired five providers under `src/services/dataProviders/utility/`.
  // The resolver tier-3 walks edges in declared order:
  // Regional queues/dockets and territory identity cannot satisfy site letters.
  //   - `substation-distance`     → HIFLD PRIMARY (only point-geometry source).
  //   - `transmission-capacity`   → HIFLD PRIMARY (voltage proxy), OASIS supplementary.
  //
  // Edges sourced from `src/services/dataProviders/utility/
  // utilityProviderEdges.ts` (re-declared inline here to keep the map
  // a self-contained `ReadonlyMap` literal — the helper module is
  // retained as the canonical reference + test surface for the edge
  // shape).
  //
  // Confidence caps mirror the CAR-2 helper (≤ 0.85); the
  // `utilityCoverageMapper` independently applies the 90d staleness
  // downgrade documented in pack §CAR-2 risk register R2.
  ['substation-distance', [
    {
      provider: 'hifld-utility',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { nearestSubstation?: unknown };
        if (r.nearestSubstation === null || typeof r.nearestSubstation !== 'object') return false;
        const ns = r.nearestSubstation as { distanceKm?: unknown };
        return typeof ns.distanceKm === 'number' && Number.isFinite(ns.distanceKm);
      },
      toCoverage: (resp) => {
        const r = resp as {
          nearestSubstation?: {
            name?: string | null;
            id?: string;
            distanceKm: number;
            voltageKv?: number | null;
          };
        };
        const ns = r.nearestSubstation!;
        return {
          rationale: `Nearest substation: ${ns.name ?? ns.id ?? '?'} at ${ns.distanceKm.toFixed(1)}km, ${ns.voltageKv ?? '?'}kV.`,
          // Closer substation = higher confidence; parallels the
          // existing `hifld-grid` edge on `utility-letter`.
          confidence: ns.distanceKm < 25 ? 0.8 : 0.6,
        };
      },
      sourceUrl: 'https://hifld-geoplatform.opendata.arcgis.com/datasets/electric-substations',
      rationale: 'HIFLD Open — only authoritative source for substation point geometry.',
    },
  ]],

  ['transmission-capacity', [
    {
      provider: 'hifld-utility',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { lineCapacityMW?: unknown };
        return typeof r.lineCapacityMW === 'number' && Number.isFinite(r.lineCapacityMW);
      },
      toCoverage: (resp) => {
        const r = resp as { lineCapacityMW?: number; interconnectionClass?: string | null };
        return {
          rationale: `Line capacity proxy: ${r.lineCapacityMW ?? '?'}MW (interconnection class: ${r.interconnectionClass ?? '?'}). OASIS / cluster study refines.`,
          confidence: 0.6,
        };
      },
      sourceUrl: 'https://hifld-geoplatform.opendata.arcgis.com/datasets/electric-substations',
      rationale: 'HIFLD voltage proxy — fast feasibility-stage capacity estimate.',
    },
    {
      provider: 'oasis-real-time',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { atcSnapshots?: unknown };
        return Array.isArray(r.atcSnapshots) && r.atcSnapshots.length > 0;
      },
      toCoverage: (resp) => {
        const r = resp as { atcSnapshots?: unknown[] };
        const n = Array.isArray(r.atcSnapshots) ? r.atcSnapshots.length : 0;
        return {
          rationale: `OASIS ATC: ${n} snapshots (cached 24h). Real-time supplement to voltage proxy.`,
          confidence: 0.7,
        };
      },
      sourceUrl: 'https://www.ferc.gov/electric/oasis',
      rationale: 'OASIS — read-only real-time ATC; supplements voltage proxy but is not a reservation.',
    },
  ]],

  // ── solar / wind resource (renewables-focused requirements) ──────
  // CAR-FOLLOWUP-NREL (wave-18) — ✅ SHIPPED.
  // Two synthetic requirement keys answered by the NREL provider chain:
  //   - `solar-resource-summary` → `nrel-solar` (GHI/DNI annual mean
  //     from NSRDB via `developer.nrel.gov/api/solar/solar_resource/v1`).
  //   - `wind-resource-summary`  → `nrel-wind` (100m hub-height wind
  //     speed + capacity factor from WIND-Toolkit v2; Open-Meteo
  //     fallback when NREL is unavailable).
  //
  // The resolver's tier-3 path reads `providerResponses['solar']` /
  // `providerResponses['wind']` (slot keys mirror `AggregatedSiteData`).
  // CAR-FOLLOWUP-CHECKLIST: the checklist surface may add these keys to
  // `documentChecklists.ts` later; for now the resolver's flexible
  // requirement-key map already accepts arbitrary string ids.
  //
  // Confidence: 0.7 (provider-direct, sub-degree spatial). Capped
  // ≤ 0.85 by `safeToCoverage` — the doc-beats-provider invariant
  // still holds.
  ['solar-resource-summary', [
    {
      provider: 'nrel-solar',
      geoScope: 'global',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { ghi?: unknown };
        // R-NUM — only surface a finite, positive GHI. NREL returns
        // kWh/m²/day; values are physically in [0, ~9]. Anything outside
        // is upstream garbage we refuse.
        return typeof r.ghi === 'number' && Number.isFinite(r.ghi) && r.ghi > 0;
      },
      toCoverage: (resp) => {
        const r = resp as { ghi?: number; dni?: number };
        const ghi = typeof r.ghi === 'number' ? r.ghi.toFixed(2) : '?';
        const dni = typeof r.dni === 'number' && Number.isFinite(r.dni) ? r.dni.toFixed(2) : '?';
        return {
          rationale: `NREL NSRDB solar resource — GHI ${ghi} · DNI ${dni} kWh/m²/day (annual mean). On-site pyranometer baseline still recommended.`,
          confidence: 0.7,
        };
      },
      sourceUrl: 'https://developer.nrel.gov/docs/solar/solar-resource-v1/',
      rationale:
        'NREL National Solar Radiation Database (NSRDB) — federal authoritative annual irradiance; site pyranometer refines.',
    },
  ]],

  ['wind-resource-summary', [
    {
      provider: 'nrel-wind',
      geoScope: 'global',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const r = resp as { windSpeed100m?: unknown };
        // R-NUM — physically plausible 100m wind speeds sit in
        // [0, 25] m/s. Anything outside is upstream garbage.
        return (
          typeof r.windSpeed100m === 'number' &&
          Number.isFinite(r.windSpeed100m) &&
          r.windSpeed100m > 0 &&
          r.windSpeed100m < 25
        );
      },
      toCoverage: (resp) => {
        const r = resp as { windSpeed100m?: number; capacityFactor?: number };
        const ws = typeof r.windSpeed100m === 'number' ? r.windSpeed100m.toFixed(1) : '?';
        const cf =
          typeof r.capacityFactor === 'number' && Number.isFinite(r.capacityFactor)
            ? `${(r.capacityFactor * 100).toFixed(0)}%`
            : '?';
        return {
          rationale: `NREL WIND-Toolkit — ${ws} m/s @ 100m, est. capacity factor ${cf}. Met-tower baseline still required for finance.`,
          confidence: 0.7,
        };
      },
      sourceUrl: 'https://developer.nrel.gov/docs/wind/wind-toolkit/',
      rationale:
        'NREL WIND Toolkit (or Open-Meteo fallback) — federal authoritative wind resource estimate; met-tower campaign refines.',
    },
  ]],

  // ── gas-supply deliverability context (Tier 1, 2026-06) ──────────
  // NON-BLOCKING / recommended-only: gas-infra proximity (compressor
  // stations / interconnects / receipt points) is operator/state-level
  // CONTEXT for tap feasibility — it can never satisfy a firm-capacity
  // requirement (delivery pressure, receipt-point MAOP, and firm
  // transport availability are not public data; FERC Form 567 system
  // flow diagrams are CEII). The requirement key is forward-wired: no
  // DOC_CHECKLISTS item carries this id yet, and the resolver's
  // REQUIREMENT_PROVIDER_SLOT has no slot for it, so tier-3 cannot
  // mark anything 'adequate' off this edge today — it registers the
  // provider row + predicate for the gas-supply coverage surfaces.
  // The expected `resp` shape is the compiled `gasInfraProximity`
  // aggregate (`FuelSupplyEvidence['gasInfraProximity']` /
  // `OnSiteInfrastructureData['gasInfraProximity']`).
  ['gas-supply-deliverability', [
    {
      provider: 'hifld-gas-infra',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const gp = (resp as { gasInfraProximity?: unknown }).gasInfraProximity ?? resp;
        if (!gp || typeof gp !== 'object') return false;
        const r = gp as {
          nearestCompressorKm?: unknown;
          nearestInterconnectKm?: unknown;
          nearestReceiptPointKm?: unknown;
        };
        const finite = (v: unknown): boolean =>
          typeof v === 'number' && Number.isFinite(v) && v >= 0;
        // Non-empty = at least one real proximity row. All-null husks
        // (no layers fetched / sentinel-only attributes) do NOT count.
        return (
          finite(r.nearestCompressorKm) ||
          finite(r.nearestInterconnectKm) ||
          finite(r.nearestReceiptPointKm)
        );
      },
      toCoverage: (resp) => {
        const gp = ((resp as { gasInfraProximity?: unknown }).gasInfraProximity ?? resp) as {
          nearestCompressorKm?: number | null;
          nearestInterconnectKm?: number | null;
          nearestReceiptPointKm?: number | null;
          receiptPointMaxCapMmcfd?: number | null;
          dataVintage?: string;
        };
        const parts: string[] = [];
        if (typeof gp.nearestCompressorKm === 'number' && Number.isFinite(gp.nearestCompressorKm)) {
          parts.push(`compressor ${gp.nearestCompressorKm.toFixed(1)} km`);
        }
        if (typeof gp.nearestInterconnectKm === 'number' && Number.isFinite(gp.nearestInterconnectKm)) {
          parts.push(`interconnect ${gp.nearestInterconnectKm.toFixed(1)} km`);
        }
        if (typeof gp.nearestReceiptPointKm === 'number' && Number.isFinite(gp.nearestReceiptPointKm)) {
          const cap =
            typeof gp.receiptPointMaxCapMmcfd === 'number' && Number.isFinite(gp.receiptPointMaxCapMmcfd)
              ? ` (max ${gp.receiptPointMaxCapMmcfd.toFixed(1)} MMcf/d)`
              : '';
          parts.push(`receipt point ${gp.nearestReceiptPointKm.toFixed(1)} km${cap}`);
        }
        return {
          rationale:
            `EIA/HIFLD gas-infra proximity (vintage ${gp.dataVintage ?? '2021-02'}): ${parts.join(' · ') || 'present'}. ` +
            'Context only — firm capacity and delivery pressure require operator engagement + interconnect study.',
          // Low floor: stale 2021-02 vintage + context-only semantics.
          confidence: 0.55,
        };
      },
      sourceUrl:
        'https://services5.arcgis.com/HDRa0B57OVrv2E1q/ArcGIS/rest/services/Natural_Gas_Compressor_Stations/FeatureServer',
      rationale:
        'EIA/HIFLD gas-infrastructure point layers (compressors / interconnects / receipt-delivery points, vintage 2021-02) — recommended tap-feasibility context; never a substitute for an operator interconnect study.',
    },
    // ── Tier 2 (2026-06): PHMSA operator system stats ─────────────
    // Same NON-BLOCKING / recommended-only contract as the Tier 1 edge
    // above. The expected `resp` shape is the compiled fuelSupply data
    // (`FuelSupplyEvidence`) — or the `operatorSystemStats` block
    // itself. Non-empty = a REAL exact/alias PHMSA match with filed
    // mileage; `matchBasis: 'none'` honesty records do NOT count.
    {
      provider: 'phmsa-operator-stats',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const oss =
          (resp as { operatorSystemStats?: unknown }).operatorSystemStats ?? resp;
        if (!oss || typeof oss !== 'object') return false;
        const r = oss as {
          matchBasis?: unknown;
          matchedName?: unknown;
          totalOnshoreTransmissionMiles?: unknown;
        };
        return (
          (r.matchBasis === 'exact' || r.matchBasis === 'alias') &&
          typeof r.matchedName === 'string' &&
          typeof r.totalOnshoreTransmissionMiles === 'number' &&
          Number.isFinite(r.totalOnshoreTransmissionMiles) &&
          r.totalOnshoreTransmissionMiles > 0
        );
      },
      toCoverage: (resp) => {
        const oss = ((resp as { operatorSystemStats?: unknown }).operatorSystemStats ?? resp) as {
          matchedName?: string | null;
          matchBasis?: string;
          reportYear?: number;
          state?: string | null;
          totalOnshoreTransmissionMiles?: number | null;
          shareGte20inPct?: number | null;
          shareSmysGte50Pct?: number | null;
          sharePre1970Pct?: number | null;
          interstate?: boolean | null;
        };
        const parts: string[] = [];
        if (typeof oss.totalOnshoreTransmissionMiles === 'number') {
          parts.push(
            `${oss.totalOnshoreTransmissionMiles.toFixed(0)} mi onshore transmission${oss.state ? ` in ${oss.state}` : ''}`,
          );
        }
        if (typeof oss.shareGte20inPct === 'number') parts.push(`${oss.shareGte20inPct.toFixed(0)}% ≥20-inch`);
        if (typeof oss.shareSmysGte50Pct === 'number') parts.push(`${oss.shareSmysGte50Pct.toFixed(0)}% >50% SMYS`);
        if (typeof oss.sharePre1970Pct === 'number') parts.push(`${oss.sharePre1970Pct.toFixed(0)}% pre-1970`);
        if (oss.interstate != null) parts.push(oss.interstate ? 'interstate' : 'intrastate');
        return {
          rationale:
            `PHMSA F7100.2-1 operator system stats (reporting year ${oss.reportYear ?? '?'}, ` +
            `${oss.matchedName ?? 'operator'}${oss.matchBasis === 'alias' ? ', alias-matched' : ''}): ` +
            `${parts.join(' · ') || 'present'}. ` +
            'Operator/state-level system context — never site-deliverable capacity or pressure.',
          // Slightly above the Tier 1 floor: annually refreshed federal
          // filings (current report year), but still context-only.
          confidence: 0.6,
        };
      },
      sourceUrl: 'https://zenodo.org/doi/10.5281/zenodo.7683351',
      rationale:
        'PHMSA gas transmission & gathering annual report (F7100.2-1) operator×state system stats — diameter-class mix, %SMYS, vintage; recommended fuel-supply context, never a substitute for an operator interconnect study.',
    },
    // ── Tier 3a (2026-06): EIA state-to-state corridor capacity ────
    // Same NON-BLOCKING / recommended-only contract. The expected `resp`
    // shape is the compiled fuelSupply data (`FuelSupplyEvidence`) — or the
    // `capacityContext` block itself. Non-empty = at least one real
    // border-crossing row. State-border corridor capacity is CONTEXT only —
    // never a site-deliverable or firm capacity available to a new shipper.
    {
      provider: 'eia-state-to-state-capacity',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const cc = (resp as { capacityContext?: unknown }).capacityContext ?? resp;
        if (!cc || typeof cc !== 'object') return false;
        const rows = (cc as { stateBorderCapacitiesMmcfd?: unknown }).stateBorderCapacitiesMmcfd;
        return Array.isArray(rows) && rows.length > 0;
      },
      toCoverage: (resp) => {
        const cc = ((resp as { capacityContext?: unknown }).capacityContext ?? resp) as {
          stateBorderCapacitiesMmcfd?: { capacityMmcfd?: number }[];
          vintageYear?: number;
        };
        const rows = Array.isArray(cc.stateBorderCapacitiesMmcfd)
          ? cc.stateBorderCapacitiesMmcfd
          : [];
        const total = rows.reduce(
          (acc, r) => acc + (typeof r.capacityMmcfd === 'number' && Number.isFinite(r.capacityMmcfd) ? r.capacityMmcfd : 0),
          0,
        );
        return {
          rationale:
            `EIA State-to-State pipeline capacity (${cc.vintageYear ?? '?'} vintage): ` +
            `~${Math.round(total).toLocaleString('en-US')} MMcf/d across ${rows.length} interstate corridor${rows.length === 1 ? '' : 's'} crossing the state. ` +
            'State/operator-level context only — never site-deliverable or firm capacity for a new shipper.',
          // Annually refreshed federal workbook (current vintage) but
          // border-corridor context, not site-deliverable — between the
          // Tier 1 floor and the Tier 2 operator-stats floor.
          confidence: 0.58,
        };
      },
      sourceUrl: 'https://www.eia.gov/naturalgas/pipelines/EIA-StatetoStateCapacity_Jan2026.xlsx',
      rationale:
        'EIA State-to-State interstate-pipeline capacity workbook — border-corridor capacity touching the site state; recommended scale-of-system context, never a substitute for an operator capacity-release / precedent agreement.',
    },
    // ── Tier 3b (2026-06): curated FERC-tariff gas-quality specs ───
    // Same NON-BLOCKING / recommended-only contract. The expected `resp`
    // shape is the compiled fuelSupply data (`FuelSupplyEvidence`) — or the
    // `qualitySpecs` block itself. Non-empty = a real curated match with the
    // tariff source `docRef`. SCREENING-LEVEL only — the operator's current
    // effective tariff is authoritative.
    {
      provider: 'gas-tariff-quality',
      geoScope: 'national-us',
      required: (resp): boolean => {
        if (!resp || typeof resp !== 'object') return false;
        const qs = (resp as { qualitySpecs?: unknown }).qualitySpecs ?? resp;
        if (!qs || typeof qs !== 'object') return false;
        const r = qs as { source?: unknown; pipelineName?: unknown; docRef?: unknown };
        return (
          r.source === 'tariff-gtc-curated' &&
          typeof r.pipelineName === 'string' &&
          typeof r.docRef === 'string'
        );
      },
      toCoverage: (resp) => {
        const qs = ((resp as { qualitySpecs?: unknown }).qualitySpecs ?? resp) as {
          pipelineName?: string;
          hhvMinBtuScf?: number;
          hhvMaxBtuScf?: number;
          co2MaxPct?: number;
          totalInertsMaxPct?: number;
          h2sMaxGrains?: number;
        };
        const parts: string[] = [];
        if (typeof qs.hhvMinBtuScf === 'number') {
          parts.push(
            typeof qs.hhvMaxBtuScf === 'number'
              ? `HHV ${qs.hhvMinBtuScf}–${qs.hhvMaxBtuScf} Btu/scf`
              : `HHV ${qs.hhvMinBtuScf} Btu/scf min`,
          );
        }
        if (typeof qs.co2MaxPct === 'number') parts.push(`CO2 ≤ ${qs.co2MaxPct}%`);
        if (typeof qs.totalInertsMaxPct === 'number') parts.push(`inerts ≤ ${qs.totalInertsMaxPct}%`);
        if (typeof qs.h2sMaxGrains === 'number') parts.push(`H2S ≤ ${qs.h2sMaxGrains} gr/100scf`);
        return {
          rationale:
            `Curated FERC-tariff gas-quality specs (${qs.pipelineName ?? 'operator'}): ` +
            `${parts.join(' · ') || 'present'}. ` +
            'Screening-level only — the operator’s current effective tariff (per receipt/delivery zone) is authoritative.',
          // Hand-seeded from a public-domain survey (not the live eTariff),
          // screening-level — lowest of the gas-context floors.
          confidence: 0.5,
        };
      },
      sourceUrl:
        'https://www.beg.utexas.edu/files/energyecon/global-gas-and-lng/CEE_Interstate_Natural_Gas_Quality_Specifications_and_Interchangeability.pdf',
      rationale:
        'Curated interstate-pipeline FERC tariff gas-quality limits (HHV / CO2 / inerts / H2S) hand-seeded from the UT-Austin CEE public-domain survey — recommended screening-level fuel-quality context, never the operator’s current effective tariff.',
    },
  ]],
]);

/**
 * Convenience helper — return the edges for `reqId`, filtered to those
 * whose `geoScope` matches `site`. Used by the resolver tier-3 path.
 * Returns `[]` for unknown reqIds or when no edge applies.
 *
 * The resolver still has to evaluate `required(resp)` against the
 * cached provider response — this helper only filters by geography.
 */
export function geoFilteredEdgesForRequirement(
  reqId: string,
  site: ProviderRequirementSite,
): ReadonlyArray<ProviderRequirementEdge> {
  const edges = PROVIDER_REQUIREMENT_MAP.get(reqId);
  if (!edges || edges.length === 0) return [];
  return edges.filter((e) => matchesGeoScope(e.geoScope, site));
}
